import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
    useStartCampaignMutation, 
    useGetEmailReportsQuery,
    useGetCampaignStatusQuery,
    useGetCampaignLogsQuery
} from '../../api/emailCampaignApi';
import './EmailCampaignDashboard.css';

const EmailCampaignDashboard = () => {
    const navigate = useNavigate();
    const [emailList, setEmailList] = useState('');
    const [activeCampaignId, setActiveCampaignId] = useState(() => {
        // LocalStorage'dan son kampanya ID'sini yükle
        return localStorage.getItem('activeCampaignId') || null;
    });
    const [showSuccess, setShowSuccess] = useState(false);
    
    const [startCampaign, { isLoading: isStarting }] = useStartCampaignMutation();
    const { data: reportsData, refetch: refetchReports } = useGetEmailReportsQuery();
    const { data: statusData, refetch: refetchStatus } = useGetCampaignStatusQuery(
        activeCampaignId,
        { skip: !activeCampaignId, pollingInterval: 2000 } // Her 2 saniyede bir güncelle
    );
    const { data: logsData } = useGetCampaignLogsQuery(
        { campaignId: activeCampaignId, limit: 100 },
        { skip: !activeCampaignId, pollingInterval: 1500 } // Her 1.5 saniyede bir log güncelle
    );

    // Debug logging
    useEffect(() => {
        if (statusData) {
            console.log('📊 Status Data:', statusData);
        }
    }, [statusData]);

    useEffect(() => {
        if (logsData) {
            console.log('📝 Logs Data:', logsData);
        }
    }, [logsData]);

    useEffect(() => {
        console.log('🔑 Active Campaign ID:', activeCampaignId);
    }, [activeCampaignId]);

    // Kampanya ID değişince LocalStorage'a kaydet
    useEffect(() => {
        if (activeCampaignId) {
            localStorage.setItem('activeCampaignId', activeCampaignId);
            console.log('Active campaign ID saved:', activeCampaignId);
        } else {
            localStorage.removeItem('activeCampaignId');
        }
    }, [activeCampaignId]);

    // Kampanya tamamlandığında ID'yi temizle
    useEffect(() => {
        if (statusData?.data?.Status === 'Completed') {
            console.log('Campaign completed, clearing active ID in 10 seconds');
            setTimeout(() => {
                setActiveCampaignId(null);
            }, 10000); // 10 saniye sonra temizle
        }
    }, [statusData?.data?.Status]);

    // Email listesini parse et
    const parseEmails = (text) => {
        const emails = text
            .split(/[\n,;]/)
            .map(email => email.trim())
            .filter(email => {
                const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                return emailRegex.test(email);
            });
        return emails;
    };

    // Kampanya başlat
    const handleStartCampaign = async () => {
        const emails = parseEmails(emailList);
        
        if (emails.length === 0) {
            alert('Lütfen geçerli email adresleri girin!');
            return;
        }

        try {
            const recipients = emails.map(email => ({ Email: email }));
            const response = await startCampaign(recipients).unwrap();
            
            console.log('Campaign response:', response);
            
            if (response.success) {
                setActiveCampaignId(response.data.reportId);
                setShowSuccess(true);
                setEmailList('');
                
                setTimeout(() => setShowSuccess(false), 5000);
                refetchReports();
            } else {
                alert('Kampanya başlatılamadı: ' + (response.message || 'Bilinmeyen hata'));
            }
        } catch (error) {
            console.error('Campaign error:', error);
            
            let errorMessage = 'Bilinmeyen hata oluştu';
            
            if (error.data) {
                errorMessage = error.data.message || error.data.title || JSON.stringify(error.data);
            } else if (error.error) {
                errorMessage = error.error;
            } else if (error.message) {
                errorMessage = error.message;
            } else if (typeof error === 'string') {
                errorMessage = error;
            }
            
            alert('Kampanya başlatılırken hata oluştu: ' + errorMessage);
        }
    };

    // Kampanya durumunu göster
    const renderCampaignStatus = () => {
        if (!statusData?.data) return null;

        const status = statusData.data;
        const isCompleted = status.Status === 'Completed';
        const progressPercentage = status.ProgressPercentage || 0;
        const statusText = status.Status || 'Unknown';
        const statusClass = statusText.toLowerCase().replace(/\s+/g, '');

        return (
            <div className="campaign-status-card">
                <h3>🚀 Aktif Kampanya</h3>
                <div className="status-info">
                    <p><strong>Kampanya Adı:</strong> {status.CampaignName || 'N/A'}</p>
                    <p><strong>Durum:</strong> 
                        <span className={`status-badge ${statusClass}`}>
                            {statusText}
                        </span>
                    </p>
                    
                    <div className="progress-section">
                        <div className="progress-bar">
                            <div 
                                className="progress-fill" 
                                style={{ width: `${progressPercentage}%` }}
                            >
                                {progressPercentage.toFixed(1)}%
                            </div>
                        </div>
                        
                        <div className="stats-grid">
                            <div className="stat-item">
                                <span className="stat-label">Toplam</span>
                                <span className="stat-value">{status.TotalEmailsToSend || 0}</span>
                            </div>
                            <div className="stat-item success">
                                <span className="stat-label">Başarılı</span>
                                <span className="stat-value">{status.SuccessfulSent || 0}</span>
                            </div>
                            <div className="stat-item error">
                                <span className="stat-label">Başarısız</span>
                                <span className="stat-value">{status.FailedToSend || 0}</span>
                            </div>
                        </div>
                    </div>
                    
                    <p className="info-text">
                        ℹ️ Tarayıcıyı kapatabilirsiniz, kampanya arka planda çalışmaya devam edecek.
                    </p>
                </div>
            </div>
        );
    };

    // Gerçek zamanlı logları göster
    const renderLiveLogs = () => {
        if (!logsData?.data || logsData.data.length === 0) return null;

        return (
            <div className="live-logs-card">
                <h3>📝 Canlı Loglar</h3>
                <div className="logs-container">
                    {logsData.data.map((log, index) => (
                        <div 
                            key={`${log.EmailAddress}-${log.AttemptTime}-${index}`} 
                            className={`log-item ${log.IsSuccessful ? 'success' : 'failed'}`}
                        >
                            <div className="log-icon">
                                {log.IsSuccessful ? '✅' : '❌'}
                            </div>
                            <div className="log-content">
                                <div className="log-email">{log.EmailAddress || 'N/A'}</div>
                                <div className="log-time">
                                    {log.AttemptTime ? new Date(log.AttemptTime).toLocaleTimeString('tr-TR') : 'N/A'}
                                </div>
                                {log.ErrorMessage && (
                                    <div className="log-error">{log.ErrorMessage}</div>
                                )}
                            </div>
                            <div className="log-status">
                                {log.IsSuccessful ? 'Gönderildi' : 'Başarısız'}
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        );
    };

    return (
        <div className="email-campaign-dashboard">
            <div className="dashboard-container">
                <header className="dashboard-header">
                    <div>
                        <h1>📧 Email Kampanya Yönetimi</h1>
                        <p>Toplu email kampanyası başlatın ve ilerlemesini takip edin</p>
                    </div>
                    <button 
                        className="template-editor-btn"
                        onClick={() => navigate('/admin/email-template-editor')}
                    >
                        ✨ HTML Template Editör
                    </button>
                    {activeCampaignId && (
                        <div className="active-campaign-indicator">
                            <span className="pulse-dot"></span>
                            Aktif kampanya takip ediliyor...
                        </div>
                    )}
                </header>

                {showSuccess && (
                    <div className="success-alert">
                        ✅ Kampanya başarıyla başlatıldı! Arka planda çalışmaya devam edecek.
                    </div>
                )}

                <div className="campaign-grid">
                    {/* Sol Panel - Kampanya Başlatma */}
                    <div className="campaign-start-section">
                        <div className="card">
                            <h2>Yeni Kampanya Başlat</h2>
                            
                            <div className="form-group">
                                <label>Email Adresleri</label>
                                <textarea
                                    className="email-textarea"
                                    placeholder="Email adreslerini her satıra bir tane gelecek şekilde girin&#10;örnek:&#10;user1@example.com&#10;user2@example.com&#10;user3@example.com"
                                    value={emailList}
                                    onChange={(e) => setEmailList(e.target.value)}
                                    rows={10}
                                />
                                <small className="form-hint">
                                    Her satıra bir email adresi veya virgül/noktalı virgül ile ayırarak girin
                                </small>
                            </div>

                            <div className="preview-section">
                                <strong>Geçerli Email Sayısı: </strong>
                                <span className="email-count">
                                    {parseEmails(emailList).length}
                                </span>
                            </div>

                            <button 
                                className="btn-start-campaign"
                                onClick={handleStartCampaign}
                                disabled={isStarting || parseEmails(emailList).length === 0}
                            >
                                {isStarting ? (
                                    <>
                                        <span className="spinner"></span>
                                        Başlatılıyor...
                                    </>
                                ) : (
                                    <>
                                        🚀 Kampanyayı Başlat
                                    </>
                                )}
                            </button>
                        </div>
                    </div>

                    {/* Sağ Panel - Durum Takibi */}
                    <div className="campaign-status-section">
                        {statusData?.data && renderCampaignStatus()}
                        
                        {/* Canlı Loglar */}
                        {logsData?.data && renderLiveLogs()}
                        
                        {/* Geçmiş Kampanyalar */}
                        <div className="card">
                            <h3>📊 Kampanya Geçmişi</h3>
                            {reportsData?.data?.length > 0 ? (
                                <div className="reports-list">
                                    {reportsData.data.slice(0, 5).map((report) => {
                                        const reportStatus = report.status || 'Unknown';
                                        const reportStatusClass = reportStatus.toLowerCase().replace(/\s+/g, '');
                                        
                                        return (
                                            <div 
                                                key={report.id} 
                                                className="report-item"
                                                onClick={() => setActiveCampaignId(report.id)}
                                            >
                                                <div className="report-header">
                                                    <strong>{report.campaignName || 'N/A'}</strong>
                                                    <span className={`status-badge ${reportStatusClass}`}>
                                                        {reportStatus}
                                                    </span>
                                                </div>
                                                <div className="report-stats">
                                                    <small>
                                                        {report.successfulSent || 0}/{report.totalEmailsToSend || 0} başarılı
                                                    </small>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            ) : (
                                <p className="no-data">Henüz kampanya bulunmuyor</p>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default EmailCampaignDashboard;
